import {LinearProgress, ThemeProvider} from '@material-ui/core';
import TreezTheme from '@treez-ui/core/styles/Theme';
import {Page} from '@treez-ui/core/layout';
import {Header} from '../widgets/header/Header';
import {Modal as UserFeedbackModal} from '@treez-ui/core/feedback/modals/Modal';
import {Snackbar} from '@treez-ui/core/feedback/modals';
import React, {FunctionComponent, useEffect} from 'react';
import {useHistory, useRouteMatch} from 'react-router';
import NoAccess from './NoAccess';
import {usePingAuthContext} from '../../lib/auth/ping/PingAuthProvider';
import {PingAuthStatus} from '../../lib/auth/ping/types/PingAuthStatus';
import {useSearchForOrganizationQuery} from './UIWrapper/useSearchForOrganizationQuery';
import {useGetUserHintsQuery} from './UIWrapper/useGetUserHintsQuery';
import {useGetCatalogUniqueProductValuesQuery} from './UIWrapper/useGetCatalogUniqueProductValuesQuery';
import {useGetBrandsFromBrandQuery} from './UIWrapper/useGetBrandsFromBrandQuery';
import {RouteParams} from './UIWrapper/RouteParams';
import {useUIWrapperMenuOptions} from './UIWrapper/useUIWrapperMenuOptions';
import {useUIWrapperRoutes} from './UIWrapper/useUIWrapperRoutes';
import {useSelector} from 'react-redux';
import {selectUserFeedback} from '../../redux/selectors/userFeedbackSelectors';
import {selectSnackbar} from '../../redux/selectors/snackbarSelectors';
import {
    selectOrganizationCatalogId,
    selectOrganizationState,
} from '../../redux/selectors/organizationSelectors';
import {useUiWrapperProductFields} from './UIWrapper/useUiWrapperProductFields';
import {useUIWrapperAccess} from './UIWrapper/useUIWrapperAccess';
import {useUIWrapperPendoInit} from './UIWrapper/useUIWrapperPendoInit';
import {selectUserHintsOrgIds} from '../../redux/selectors/userSelectors';
import _ from 'lodash';

export const DEFAULT_ROUTABLE_ORG_ID = 0;
export const UIWrapper: FunctionComponent<{}> = (props: React.PropsWithChildren<{}>) => {
    const match = useRouteMatch<RouteParams>();
    const {
        params: {organizationId},
    } = match;
    const userFeedback = useSelector(selectUserFeedback);
    const snackbar = useSelector(selectSnackbar);
    const orgCatalogId = useSelector(selectOrganizationCatalogId);
    const userOrgIds = useSelector(selectUserHintsOrgIds);

    const productFields = useUiWrapperProductFields();
    const parentOrgId = _.head(userOrgIds) || DEFAULT_ROUTABLE_ORG_ID;
    const {status, logIn, user} = usePingAuthContext();
    const {hasAccess} = useUIWrapperAccess({organizationId, userOrgIds, user});
    const {centralCatalogUrl} = useUIWrapperRoutes(parentOrgId);
    const {menuOptions, menuOptionsDash} = useUIWrapperMenuOptions(parentOrgId);
    const history = useHistory();
    const searchOrganizationQuery = useSearchForOrganizationQuery(organizationId);
    const catalogUniqueProductValuesQuery = useGetCatalogUniqueProductValuesQuery(
        orgCatalogId,
        productFields
    );
    const organization = useSelector(selectOrganizationState);
    useUIWrapperPendoInit(user, organization);
    const getUserHintsQuery = useGetUserHintsQuery(user.id);
    const getBrandsFromBrandQuery = useGetBrandsFromBrandQuery();

    const redirect = (pathname: string, data: any = null) => {
        history.push({
            pathname,
        });
    };

    const renderContent = () => {
        // Render content if access is allowed
        let progress = 0;
        if (
            searchOrganizationQuery.isSuccess &&
            catalogUniqueProductValuesQuery.isSuccess &&
            getUserHintsQuery.isSuccess &&
            getBrandsFromBrandQuery.isSuccess
        ) {
            return hasAccess ? props.children : <NoAccess />;
        }
        if (
            searchOrganizationQuery.isError ||
            catalogUniqueProductValuesQuery.isError ||
            getUserHintsQuery.isError ||
            getBrandsFromBrandQuery.isError
        ) {
            return (
                <>
                    {searchOrganizationQuery.isError && <p>Unable to fetch organization.</p>}
                    {catalogUniqueProductValuesQuery.isError && <p>Unable to fetch catalog.</p>}
                    {getUserHintsQuery.isError && <p>Unable to fetch hints.</p>}
                    {getBrandsFromBrandQuery.isError && <p>Unable to fetch brands.</p>}
                </>
            );
        }

        progress += searchOrganizationQuery.isSuccess ? 25 : 0;
        progress += catalogUniqueProductValuesQuery.isSuccess ? 25 : 0;
        progress += getUserHintsQuery.isSuccess ? 25 : 0;
        progress += getBrandsFromBrandQuery.isSuccess ? 25 : 0;

        return (
            <LinearProgress variant="determinate" value={progress} style={{marginTop: '20px'}} />
        );
    };

    useEffect(() => {
        // Redirects user to login if it's not logged in or in error state.
        if (status === PingAuthStatus.NOT_LOGGED_IN || status === PingAuthStatus.ERROR) {
            logIn();
        }
    }, [status]);

    return (
        <ThemeProvider theme={TreezTheme}>
            <Page>
                <div style={{paddingLeft: '30px'}}>
                    <Header
                        onLogoClick={() => redirect(centralCatalogUrl)}
                        menuItems={menuOptions}
                        menuItemsDash={menuOptionsDash}
                    />
                </div>
                {renderContent()}
                <UserFeedbackModal {...userFeedback} />
                <Snackbar {...snackbar} />
            </Page>
        </ThemeProvider>
    );
};

export default UIWrapper;
